/**
 * Account service — the signed-in user's profile (name + email).
 */

import { api } from "./base_service";
import { getInfo } from "./info_service";
import { getUser } from "./company_service";
import type { ApiError, Result } from "./types";
import { apiEndpoints } from "@/src/config/api/endpoints";

/** Identity providers we offer on the Account page. */
export type SsoProviderId = "github" | "microsoft" | "google" | "okta";

export type Profile = {
  id: string;
  firstName: string;
  lastName: string;
  email: string;
};

export type UpdateProfileInput = {
  firstName: string;
  lastName: string;
};

export type ChangePasswordInput = {
  currentPassword: string;
  newPassword: string;
};

/** Split a single display name into first / last (last = everything after the first token). */
function splitName(name: string | null): { firstName: string; lastName: string } {
  const parts = (name ?? "").trim().split(/\s+/).filter(Boolean);
  return { firstName: parts[0] ?? "", lastName: parts.slice(1).join(" ") };
}

/**
 * Fetch the currently signed-in user's profile (name + email).
 *
 * Sources name + email from GET /info/, then enriches first/last from
 * GET /user/{uuid}. If that enrichment read fails, falls back to splitting the
 * single `name` from /info/ so the page still renders.
 */
export async function getMe(): Promise<Result<Profile>> {
  try {
    const info = await getInfo();
    const user = info.user;

    let { firstName, lastName } = splitName(user.name);
    const detail = await getUser(user.uuid);
    if (detail.ok) {
      firstName = detail.value.firstName || firstName;
      lastName = detail.value.lastName || lastName;
    }

    return {
      ok: true,
      value: {
        id: user.uuid,
        firstName,
        lastName,
        email: user.email,
      },
    };
  } catch (err) {
    return { ok: false, error: err as ApiError };
  }
}

/**
 * Fetch the providers the user has connected via SSO.
 *
 * No per-provider read endpoint exists on the DRF backend (the social-account
 * contract is held pending the OAuth round-trip — see sso_service). Returns an
 * empty list; the Account page's linked-accounts section is hidden meanwhile.
 */
export async function getLinkedAccounts(): Promise<Result<SsoProviderId[]>> {
  return { ok: true, value: [] };
}

/**
 * Update first / last name on the user's profile via PATCH /user/{uuid}.
 *
 * NOTE: the ref client only ever PATCHed role/monitoring fields on this route;
 * the name-write permission on staging is being verified. If the backend rejects
 * or ignores name fields, this becomes a handled-error stub (flagged to Hector).
 */
export async function updateProfile(input: UpdateProfileInput): Promise<Result<void>> {
  try {
    const info = await getInfo();
    await api.patch(apiEndpoints.account().user().update(info.user.uuid), {
      first_name: input.firstName,
      last_name: input.lastName,
    });
    return { ok: true, value: undefined };
  } catch (err) {
    return { ok: false, error: err as ApiError };
  }
}

/**
 * Persist the user's onboarding opt-out via PATCH /user/{uuid}.
 * Sets `opt_out: true` so the welcome popup is never shown again on login.
 * Fire-and-forget safe: the caller dismisses the popup locally regardless
 * of the result; a failure only means the popup reappears on the next session.
 */
export async function optOutOnboarding(): Promise<Result<void>> {
  try {
    const info = await getInfo();
    await api.patch(apiEndpoints.account().user().optOut(info.user.uuid), { opt_out: true });
    return { ok: true, value: undefined };
  } catch (err) {
    return { ok: false, error: err as ApiError };
  }
}
